<template>
  <div class="ppa-overview card mb-2">
    <div class="card-body p-3">
      <!-- Header Section -->
      <div class="d-flex justify-content-between align-items-start mb-3">
        <div>
          <!-- Title -->
          <h4 class="mb-1 font-serif">
            {{ portfolio.name || $t("ppa_strategy.ppa_agreement") }}
          </h4>
          <!-- Company Name (only in multi-company mode) -->
          <div v-if="companyName" class="mb-2">
            <small class="text-muted">{{ companyName }}</small>
          </div>
          <!-- Action Details Chips -->
          <div v-if="portfolio.action_instructions" class="d-flex gap-1 mb-2">
            <span class="badge bg-primary">
              {{
                $t(
                  `production_strategy.${portfolio.action_instructions.transaction_type.toLowerCase()}`
                )
              }}
            </span>
            <span v-if="usesCnmcIssuance" class="badge bg-secondary">
              {{ $t("cnmc.ppa_execution_summary") }}
            </span>
            <span v-else class="badge bg-secondary">
              {{
                $t(
                  `production_strategy.${portfolio.action_instructions.delivery_method.toLowerCase()}`
                )
              }}
            </span>
            <span
              v-if="
                portfolio.action_instructions.send_template?.recipient_cnmc_r2
              "
              class="badge bg-warning-subtle text-warning-emphasis"
              >CNMC ·
              {{
                portfolio.action_instructions.send_template.recipient_cnmc_r2
              }}</span
            >
            <span v-if="!usesCnmcIssuance" class="badge bg-info">
              {{
                formatDeliveryTiming(
                  portfolio.action_instructions.delivery_timing
                )
              }}
            </span>
          </div>
        </div>
        <!-- Edit Button Slot -->
        <slot name="edit-button"></slot>
      </div>

      <!-- Allocation Section -->
      <div class="mb-3">
        <div class="d-flex align-items-baseline mb-2">
          <strong class="me-2"
            >{{ formatMwh(projectedTotal) }} {{ $t("common.mwh") }}</strong
          >
          <small class="text-muted">{{ allocationType }}</small>
        </div>
      </div>

      <!-- Next Delivery Date -->
      <div v-if="nextDeliveryDate" class="mt-1">
        <small class="text-muted">
          {{ $t("ppa_strategy.next_delivery") }}: {{ nextDeliveryDate }}
        </small>
      </div>

      <!-- Registry Devices Section -->
      <div
        v-if="assignedDevices.length > 0"
        class="device-section border-top pt-3"
      >
        <div class="device-badges d-flex flex-wrap gap-2">
          <span
            v-for="device in assignedDevices"
            :key="device.id"
            class="device-badge"
          >
            <TechnologyIcon
              :technology="
                getDeviceTechnology(device.id, props.registryDevices || [])
              "
              iconClass="me-1 text-muted"
            />
            {{ device.device_name }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { hasCnmcConnection } from "@/dashboard/production_strategy/issuance-reporting/es/cnmcPresentation";
import TechnologyIcon from "@/components/common/TechnologyIcon.vue";
import { formatMwh } from "@/composables/formatUtils";
import { getDeviceTechnology } from "@/dashboard/production_strategy/overview/PPA/ppaDeviceUtils";
import { formatPercentageDisplay } from "@/dashboard/production_strategy/overview/PPA/ppaPortfolioUtils";
import type {
  Company,
  PortfolioWithInstructions,
  RegistryDevice,
} from "@/types";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import type { PPAIndividualAllocation } from "../useProductionAllocationData";
import { calculateNextExecutionDate as calculateNextDeliveryDate } from "./ppaExecutionUtils";

const props = defineProps<{
  portfolio: PortfolioWithInstructions;
  companies?: Company[];
  registryDevices?: RegistryDevice[];
  individualAllocation?: PPAIndividualAllocation | null;
}>();

const { t } = useI18n();

const usesCnmcIssuance = computed(() =>
  hasCnmcConnection(
    props.companies?.find(
      (company) => company.id === props.portfolio.company_id
    )?.registry_connections
  )
);

// Multi-company mode detection
const isMultiCompany = computed(() => (props.companies?.length || 0) > 1);

// Get company name for this portfolio
const companyName = computed(() => {
  if (!isMultiCompany.value || !props.companies) return null;

  const company = props.companies.find(
    (c) => c.id === props.portfolio.company_id
  );
  return company?.name || null;
});

// Calculate projected total from individual allocation data (backend calculated)
const projectedTotal = computed(() => {
  // Use the backend-calculated allocation if available
  if (props.individualAllocation?.yearly_allocation) {
    return props.individualAllocation.yearly_allocation;
  }

  // Fallback to portfolio limits calculation (legacy)
  let total = 0;
  if (props.portfolio.portfolio_instructions?.limits) {
    props.portfolio.portfolio_instructions.limits.forEach((limit) => {
      total += limit.limit_mwh || 0;
    });
  }
  return total;
});

// Determine allocation type
const allocationType = computed(() => {
  // Use backend allocation type if available
  if (props.individualAllocation?.allocation_type) {
    const allocType = props.individualAllocation.allocation_type;

    if (
      allocType === "percentage" &&
      props.individualAllocation.allocate_percentage
    ) {
      return t("ppa_strategy.percentage_allocation", {
        percentage: formatPercentageDisplay(
          props.individualAllocation.allocate_percentage
        ),
      });
    }

    if (allocType === "fixed") {
      return t("ppa_strategy.fixed_yearly_allocation");
    }
  }

  // Fallback to portfolio instructions (legacy)
  const instructions = props.portfolio.portfolio_instructions;
  if (!instructions) return "";

  // Check if it's percentage based
  if (
    instructions.allocate_percentage &&
    instructions.allocate_percentage > 0
  ) {
    return t("ppa_strategy.percentage_allocation", {
      percentage: formatPercentageDisplay(instructions.allocate_percentage),
    });
  }

  // Default to fixed yearly allocation
  return t("ppa_strategy.fixed_yearly_allocation");
});

// Calculate next delivery date based on action instructions
const nextDeliveryDate = computed(() => {
  if (usesCnmcIssuance.value) return null;
  if (!props.portfolio.action_instructions) return null;

  return calculateNextDeliveryDate(props.portfolio.action_instructions);
});

// Get assigned devices
const assignedDevices = computed(() => {
  if (!props.registryDevices) {
    return [];
  }

  let deviceIds: string[] = [];
  if (props.individualAllocation?.registry_device_ids) {
    deviceIds = props.individualAllocation.registry_device_ids;
  } else if (props.portfolio.portfolio_instructions?.registry_device_ids) {
    deviceIds = props.portfolio.portfolio_instructions.registry_device_ids;
  }

  return props.registryDevices.filter((device) =>
    deviceIds.includes(device.id.toString())
  );
});

// Format delivery timing for display
const formatDeliveryTiming = (timing: string) => {
  if (timing === "FIXED_DATES") {
    const dateCount =
      props.portfolio.action_instructions?.recurring_date_specs?.length || 0;
    return t("production_strategy.fixed_dates_count", { count: dateCount });
  }
  return t(`production_strategy.${timing.toLowerCase()}`);
};
</script>

<style scoped lang="scss">
.ppa-overview {
  border: 1px solid #e5e7eb;
  background-color: #ffffff;
  border-radius: 8px;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .device-badge {
    background-color: var(--phoenix-gray-50);
    border: 1px solid var(--phoenix-gray-300);
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 0.75rem;
    color: var(--phoenix-gray-700);
  }

  .device-section {
    border-color: #e5e7eb !important;
  }
}
</style>
